import { civilDateBounds, isCivilDate } from './civil-date';

/**
 * Saisie au pavé du formulaire de gabarit, comme la tablette (`form_pad.dart`, ADR-141 §3 bis) :
 * une date civile tapée JJ/MM/AA (ou JJ/MM/AAAA), mise en forme pendant la frappe, et une chaîne `keypad` faite de
 * chiffres (les zéros de tête comptent). Fonctions pures, rejouées sur le jeu de cas partagé
 * `pad-input.cases.json`. Le serveur reste juge de la réponse.
 */

/** Une date tapée face au calendrier et à ses bornes. */
export type PadDateState = 'empty' | 'partial' | 'impossible' | 'out_of_range' | 'ambiguous' | 'valid';

type Bounds = { min: string; max: string } | null;

/** Touche appliquée à un texte du pavé : chiffre, `⌫` (efface), `C` (vide) ; toute autre est ignorée. */
function applyKey(digits: string, key: string, limit: number): string {
  if (key === 'C') return '';
  if (key === '⌫') return digits.slice(0, -1);
  return /^\d$/.test(key) && digits.length < limit ? `${digits}${key}` : digits;
}

/** Chiffres → `JJ/MM/AA` (ou `JJ/MM/AAAA`) au fil de la frappe (« 1710 » → « 17/10 »). */
export function padDateText(digits: string): string {
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Texte de date après une suite de touches, à partir d'un texte affiché (huit chiffres au plus). */
export function typeDate(text: string, keys: string): string {
  let digits = text.replace(/\D/g, '').slice(0, 8);
  for (const key of keys) digits = applyKey(digits, key, 8);
  return padDateText(digits);
}

/**
 * Texte saisi au clavier physique (ou collé) → texte du pavé : seuls les chiffres comptent, les
 * barres se remettent d'elles-mêmes (« 17/10/2026 », « 17102026 » et « 17.10.2026 » donnent la même date).
 */
export function padDateFromTyping(raw: string): string {
  return padDateText(raw.replace(/\D/g, '').slice(0, 8));
}

/** Le jour existe-t-il au calendrier ? → date civile AAAA-MM-JJ, sinon `null`. */
function civilOf(day: number, month: number, year: number): string | null {
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const lengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month < 1 || month > 12 || day < 1 || day > lengths[month - 1]!) return null;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Lecture d'une date tapée `JJ/MM/AA` ou `JJ/MM/AAAA` (addendum (2) d'ADR-141 du 2026-10-19). Avec deux
 * chiffres d'année, le siècle est celui qui place la date dans les bornes du calendrier du champ ; sans
 * bornes (aucun fuseau connu), 20AA. Deux siècles possibles : ambiguë ; aucun : hors période. Même règle
 * que la tablette (`padDateResolve`, jeu partagé pad-input.cases.json).
 */
export function padDateResolve(text: string, bounds: Bounds): { state: PadDateState; civil: string | null } {
  if (text === '') return { state: 'empty', civil: null };
  const match = /^(\d{2})\/(\d{2})\/(\d{2}|\d{4})$/.exec(text);
  if (match === null) return { state: 'partial', civil: null };
  const day = Number(match[1]);
  const month = Number(match[2]);
  const written = Number(match[3]);
  let years: number[];
  if (match[3]!.length === 4) years = [written];
  else if (bounds === null) years = [2000 + written];
  else {
    const low = Math.floor(Number(bounds.min.slice(0, 4)) / 100) - 1;
    const high = Math.floor(Number(bounds.max.slice(0, 4)) / 100) + 1;
    years = [];
    for (let century = low; century <= high; century += 1) years.push(century * 100 + written);
  }
  const existing = years.map((year) => civilOf(day, month, year)).filter((civil): civil is string => civil !== null);
  if (existing.length === 0) return { state: 'impossible', civil: null };
  if (bounds === null) return { state: 'valid', civil: existing[0]! };
  const inside = existing.filter((civil) => civil >= bounds.min && civil <= bounds.max);
  if (inside.length === 1) return { state: 'valid', civil: inside[0]! };
  if (inside.length > 1) return { state: 'ambiguous', civil: null };
  return { state: 'out_of_range', civil: existing.length === 1 ? existing[0]! : null };
}

/** Date tapée → date civile AAAA-MM-JJ quand elle est déterminée ; sinon `null`. */
export function padDateCivil(text: string, bounds: Bounds = null): string | null {
  return padDateResolve(text, bounds).civil;
}

/** Date civile AAAA-MM-JJ → texte du pavé `JJ/MM/AA` ; autre chose (instant, format libre) → vide. */
export function civilToPadText(value: unknown): string {
  if (!isCivilDate(value)) return '';
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year!.slice(2)}`;
}

/** Même date civile décalée de `days` jours, sans fuseau (arithmétique de calendrier seulement). */
export function addCivilDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return `${String(shifted.getUTCFullYear()).padStart(4, '0')}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}`;
}

/** État d'une date tapée : vide, incomplète, impossible, hors des bornes, ambiguë, ou valable. */
export function padDateState(text: string, bounds: Bounds): PadDateState {
  return padDateResolve(text, bounds).state;
}

/** Réponse d'une date tapée : la date civile si elle est valable, sinon rien (le brouillon reste affiché). */
export function padDateValue(text: string, bounds: Bounds): string | null {
  const resolved = padDateResolve(text, bounds);
  return resolved.state === 'valid' ? resolved.civil : null;
}

const WEEKDAYS: Readonly<Record<string, number>> = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 7 };

/** Jour de la semaine d'une date civile, lundi = 1 … dimanche = 7. */
function weekdayOf(civil: string): number {
  const [year, month, day] = civil.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

/** Samedi ou dimanche (teinte de la grille des jours). */
export function isWeekend(civil: string): boolean {
  return weekdayOf(civil) >= 6;
}

/** Date d'un jeton de date rapide (`presentation.quick_dates`) à partir d'aujourd'hui ; jeton inconnu : `null`. */
export function quickDateOf(token: string, today: string): string | null {
  if (token === 'today') return today;
  if (token === 'yesterday') return addCivilDays(today, -1);
  if (token === 'day_before_yesterday') return addCivilDays(today, -2);
  const [kind, day] = token.split(':');
  const weekday = day === undefined ? undefined : WEEKDAYS[day];
  if (weekday === undefined || (kind !== 'last' && kind !== 'previous')) return null;
  const back = (weekdayOf(today) - weekday + 7) % 7;
  return addCivilDays(today, -back - (kind === 'previous' ? 7 : 0));
}

/**
 * Dates rapides d'un champ : les jetons dans l'ordre déclaré, sans doublon de date, dans les bornes
 * (données, sinon celles du calendrier autour d'aujourd'hui) ; sans aujourd'hui, aucune. Comme la tablette.
 */
export function quickDates(tokens: readonly string[], today: string | null, bounds: Bounds = null): Array<{ token: string; date: string }> {
  if (today === null) return [];
  const range = bounds ?? civilDateBounds(today);
  const out: Array<{ token: string; date: string }> = [];
  for (const token of tokens) {
    const date = quickDateOf(token, today);
    if (date === null || out.some((entry) => entry.date === date)) continue;
    if (range !== null && (date < range.min || date > range.max)) continue;
    out.push({ token, date });
  }
  return out;
}

/** Jours récents de « Plus de dates » : d'aujourd'hui à la borne basse, du plus récent au plus ancien. */
export function recentDays(today: string, bounds: Bounds, fallback = 30): string[] {
  const out: string[] = [];
  for (let day = today; out.length < 1000; day = addCivilDays(day, -1)) {
    if (bounds !== null ? day < bounds.min : out.length >= fallback) break;
    out.push(day);
  }
  return out;
}

/** Libellé d'un jour comme le v1 : « Aujourd'hui · samedi 18/10 », « Hier · … », puis « vendredi 17/10 ». */
export function quickDateLabel(civil: string, today: string, locale = 'fr-FR'): string {
  const [year, month, day] = civil.split('-').map(Number) as [number, number, number];
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, day)));
  const text = `${weekday} ${civil.slice(8, 10)}/${civil.slice(5, 7)}`;
  if (civil === today) return `Aujourd’hui · ${text}`;
  if (civil === addCivilDays(today, -1)) return `Hier · ${text}`;
  return text;
}

/** Raccourcis du v1 et de la tablette : décalage depuis aujourd'hui. */
export const DATE_SHORTCUTS = [
  { days: 0, label: 'Aujourd’hui' },
  { days: -1, label: 'Hier' },
  { days: -2, label: 'Avant-hier' },
] as const;

/** « − 1 jour » / « + 1 jour » : décale la date tapée si elle existe, sinon part d'aujourd'hui (sans décalage). */
export function padDateStep(text: string, days: number, today: string | null): string {
  const civil = padDateCivil(text, civilDateBounds(today));
  const base = civil ?? today;
  if (base === null) return text;
  return civilToPadText(addCivilDays(base, civil === null ? 0 : days));
}

/** Date relue en toutes lettres (« Samedi 17 octobre 2026 ») : un jour de calendrier, sans fuseau. */
export function longCivilDate(civil: string, locale = 'fr-FR'): string {
  const [year, month, day] = civil.split('-').map(Number) as [number, number, number];
  const text = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, day)));
  return text === '' ? text : `${text[0]!.toUpperCase()}${text.slice(1)}`;
}

/** Longueur par défaut d'une suite de chiffres sans `validation.max_length` (comme la tablette). */
export const DIGITS_MAX_DEFAULT = 40;

/** Texte d'une chaîne `keypad` après une suite de touches : chiffres seulement, `max_length` au plus. */
export function typeDigits(text: string, keys: string, maxLength?: number): string {
  let digits = text.replace(/\D/g, '');
  for (const key of keys) digits = applyKey(digits, key, maxLength ?? DIGITS_MAX_DEFAULT);
  return digits;
}

/** Saisie au clavier physique (ou collée) d'une chaîne `keypad` : chiffres gardés, coupés à `max_length`. */
export function digitsFromTyping(raw: string, maxLength?: number): string {
  return raw.replace(/\D/g, '').slice(0, maxLength ?? DIGITS_MAX_DEFAULT);
}

/**
 * La chaîne répond-elle à `validation.pattern` ? Même lecture que le serveur (expression non
 * ancrée, drapeau `u`) ; un motif invalide ne bloque rien ici, le serveur tranche.
 */
export function digitsMatchPattern(text: string, pattern: string | undefined): boolean {
  if (pattern === undefined || text === '') return true;
  try {
    return new RegExp(pattern, 'u').test(text);
  } catch {
    return true;
  }
}
